<script setup lang="ts">
import { RequestCreateHubspotPushLeadAdOnboarding } from '@yescapa-dev/ysc-api-js/modern'
import AppModalYesboonyCreateCamper from '~/components/App/Modal/AppModalYesboonyCreateCamper.vue'
import { DRAWING_CONVERTED_VAN_SURF, DRAWING_LOW_PROFILE_BIKES, DRAWING_T6_TOPUP } from '~/constants/pictures'
import { ONBOARDING_START_EVENT } from '~/utils/analytics/onboarding'
import { YSC_API_CAMPER_ERROR, YSC_API_VEHICLE_ERROR } from '~/utils/error/YscErrorClasses'

const { currentStep, onStepSuccess } = useAppFunnelInject()

definePageMeta({
  middleware: [
    function (to) {
      const { camper } = storeToRefs(useCamperStore())
      /*
      * User come from d/campers/:id page and had already loaded data
      */
      if (to.query.complete && camper.value) {
        return navigateTo({ name: 'onboarding-step-vehicle-type' })
      }
    },
  ],
})

const { resetCamper, setCamperVehicle, fetchVehicle, fetchCampers } = useCamperStore()
const { allIncomplete } = storeToRefs(useCamperStore())

const { storedVehicle } = useStoredVehicle()

const hasSelected = ref(false)

const vehicleTypes = [
  {
    name: useVehicleType({ type: 'campervan' }),
    image: DRAWING_CONVERTED_VAN_SURF,
    bgColor: 'bg-peacock-100',
  },
  {
    name: useVehicleType({ type: 'van' }),
    image: DRAWING_T6_TOPUP,
    bgColor: 'bg-pink-100',
  },
  {
    name: useVehicleType({ type: 'lowprofile' }),
    image: DRAWING_LOW_PROFILE_BIKES,
    bgColor: 'bg-yellow-100',
  },
]

const { status, error } = await useAsyncData('onboarding-step-index', async () => {
  await fetchCampers()
  return true
})
const { isPending } = useIsPending(status)

if (error.value) {
  const { $errorManager } = useErrorManager()
  $errorManager({ e: error.value, name: YSC_API_CAMPER_ERROR })
  throw createError(error.value)
}

const { user } = storeToRefs(useUserStore())
const { $apiConsumer } = useNuxtApp()
const onSubmit = async () => {
  if (!hasSelected.value) {
    resetCamper()
  }
  if (!onStepSuccess || !user.value) {
    return
  }
  try {
    $apiConsumer.send(new RequestCreateHubspotPushLeadAdOnboarding({
      payload: { idUser: user.value.id },
    }))
  }
  catch (e) {
    console.error(e)
  }
  await onStepSuccess()
}

const select = async (id: number | string | null) => {
  hasSelected.value = true
  resetCamper()
  if (id === null) {
    setCamperVehicle(storedVehicle.value)
  }
  else {
    try {
      await fetchVehicle({ id: +id })
    }
    catch (e) {
      if (e instanceof Error) {
        const { $errorManager } = useErrorManager()
        $errorManager({ e, name: YSC_API_VEHICLE_ERROR })
      }
    }
  }
  if (onStepSuccess) {
    onStepSuccess()
  }
}

const showGoboonyModal = computed(() =>
  !user.value?.permissions.can_publish_camper,
)

const { goboonyCreateCamperUrl } = useGoboonyRedirectCreateCamper(() => user?.value?.language ?? undefined)

const { event } = useGtag()
event(ONBOARDING_START_EVENT)
</script>

<template>
  <div>
    <AppModalYesboonyCreateCamper
      v-if="showGoboonyModal"
      :country="user?.language ?? undefined"
      :redirect-url="goboonyCreateCamperUrl"
    />
    <h1 class="text-center text-xl font-semibold md:text-2xl lg:text-3xl pt-2">
      {{ $t('pages.onboarding.start.create') }}
    </h1>

    <div class="-mx-4 flex justify-center md:max-w-sm max-w-[100vw] overflow-hidden md:overflow-visible py-10 md:mx-auto">
      <AppCamperTypeCard
        v-for="vehicle in vehicleTypes"
        :key="`vehicle_type-${vehicle.name}`"
        :bg-color="vehicle.bgColor"
        :image="vehicle.image"
        class="basis-1/3"
      >
        <template #image>
          <div class="w-40 md:w-52">
            <TwicImg
              :src="vehicle.image"
              :alt="`${vehicle.name} - Yescapa`"
              bot="cover=300x300"
              ratio="1180/560"
              mode="contain"
              placeholder="preview"
            />
          </div>
        </template>
      </AppCamperTypeCard>
    </div>

    <ul class="space-y-2 list-decimal">
      <li>
        <h3 class="font-semibold">
          {{ $t('pages.onboarding.start.vehicle.title') }}
        </h3>
        <p>{{ $t('pages.onboarding.start.vehicle.description') }}</p>
      </li>
      <li>
        <h3 class="font-semibold">
          {{ $t('pages.onboarding.start.location.title') }}
        </h3>
        <p>{{ $t('pages.onboarding.start.location.description') }}</p>
      </li>
      <li>
        <h3 class="font-semibold">
          {{ $t('pages.onboarding.start.pictures.title') }}
        </h3>
        <p>{{ $t('pages.onboarding.start.pictures.description') }}</p>
      </li>
      <li>
        <h3 class="font-semibold">
          {{ $t('pages.onboarding.start.prices.title') }}
        </h3>
        <p>{{ $t('pages.onboarding.start.prices.description') }}</p>
      </li>
    </ul>

    <AppLoading v-if="isPending" />

    <div v-else>
      <YscForm
        id="vehicle-selection"
        class="mt-12"
      >
        <AppVehicleList
          v-for="draft in allIncomplete"
          :id="draft.vehicle_pk"
          :key="draft.id"
          :vehicle-type="draft.vehicle_type_as_int"
          :registration="draft.vehicle_immatriculation"
          :action="select"
        />
        <AppVehicleList
          v-if="storedVehicle"
          :id="null"
          :vehicle-type="storedVehicle.type"
          :registration="storedVehicle.registration"
          :action="select"
        />
      </YscForm>
    </div>
    <YscForm
      v-if="currentStep?.idForm"
      :id="currentStep.idForm"
      :submit-action="onSubmit"
    />
  </div>
</template>
